import { useQuery } from '@tanstack/react-query'
import { Wallet } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { useAppStore } from '@/stores/app'
import {
  supplierDepositApi,
  DEPOSIT_TYPE_LABELS,
  type DepositLog,
} from '@/api/supplier'

// 金额格式化
function formatAmount(amount: number): string {
  return amount.toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

// 日期格式化（MM-DD HH:mm）
function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${mm}-${dd} ${hh}:${mi}`
}

// 单条变动记录
function DepositLogItem({ log }: { log: DepositLog }) {
  const isPositive = log.amount > 0

  return (
    <div className="flex flex-col gap-0.5 py-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {DEPOSIT_TYPE_LABELS[log.type] || log.type}
        </span>
        <span className={cn(
          'text-xs font-medium',
          isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400',
        )}>
          {isPositive ? '+' : ''}{formatAmount(log.amount)}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground/60">{formatDate(log.created_at)}</span>
        <span className="text-[11px] text-muted-foreground">
          余额 {formatAmount(log.balance_after)}
        </span>
      </div>
    </div>
  )
}

export function DepositBadge() {
  const navigate = useNavigate()
  const user = useAppStore((state) => state.user.user)

  // 非供应商不渲染
  if (!user?.crmeb_uid) return null

  return <DepositBadgeInner onNavigate={() => navigate('/supplier/deposit')} />
}

function DepositBadgeInner({ onNavigate }: { onNavigate: () => void }) {
  const { data: balance } = useQuery({
    queryKey: ['supplier', 'deposit', 'balance'],
    queryFn: () => supplierDepositApi.getBalance(),
  })

  const { data: logsData } = useQuery({
    queryKey: ['supplier', 'deposit', 'logs', { page: 1, page_size: 5 }],
    queryFn: () => supplierDepositApi.getLogs({ page: 1, page_size: 5 }),
  })

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 gap-1.5 px-2.5">
          <Wallet className="h-4 w-4 text-muted-foreground" />
          <Badge variant="secondary" className="font-mono text-xs px-1.5 py-0">
            ¥{balance !== undefined ? formatAmount(balance) : '--'}
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>保证金变动</span>
          <span className="text-xs font-normal text-muted-foreground">
            余额 ¥{balance !== undefined ? formatAmount(balance) : '--'}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {logsData?.list && logsData.list.length > 0 ? (
          <div className="max-h-[240px] overflow-y-auto px-2">
            {logsData.list.map((log) => (
              <DepositLogItem key={log.id} log={log} />
            ))}
          </div>
        ) : (
          <div className="py-4 text-center text-xs text-muted-foreground">
            暂无变动记录
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="justify-center text-accent cursor-pointer"
          onClick={onNavigate}
        >
          查看全部
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
